/// <reference types="zone.js" />

import { setCloneDeepImpl } from '@cosmos/util-common/clone-deep';
import { setGroupByImpl } from '@cosmos/util-common/group-by';

import { loadScript } from './utils';

const polyfills: Promise<void>[] = [];

if (global_isBrowser) {
  // V8 keeps 10 stack frames by default, which is often not enough to reach our
  // own code once an error crosses framework internals, so Rollbar reports arrive
  // with no actionable frame. Chromium only: Firefox and Safari ignore this.
  (Error as { stackTraceLimit?: number }).stackTraceLimit = 20;

  // We have users still running Mac OS X 10 with Safari 12.1.2; it does not support web animations,
  // so they end up having bugs like `animate is not a function`.
  if (
    /* supportsWebAnimations */ typeof Element.prototype.animate !== 'function'
  ) {
    polyfills.push(loadScript('/assets/js/web-animations-js.min.js'));
  }

  // We've noticed that many of our users are still running browsers that do not support
  // the newest ES features (e.g. `Object.fromEntries`). Potentially this list might become
  // bigger if we notice that our users require other polyfills.
  if (typeof Object.fromEntries !== 'function') {
    polyfills.push(loadScript('/assets/js/core-js-from-entries.min.js'));
  }

  // Safari in Lockdown Mode removes FileReader. Blob.arrayBuffer() still works,
  // so we rebuild the part of FileReader that we and our dependencies use.
  if (typeof FileReader === 'undefined') {
    polyfills.push(
      import('./file-reader').then(({ installFileReaderPolyfill }) =>
        installFileReaderPolyfill()
      )
    );
  }
}

interface LoadPolyfillsOptions {
  zoneless: boolean;
}

export async function loadPolyfills(
  options: LoadPolyfillsOptions
): Promise<void> {
  if (ngServerMode) {
    throw new Error('loadPolyfills() should not be called on server.');
  }

  polyfills.push(setGroupByImpl());
  polyfills.push(setCloneDeepImpl());

  // eslint-disable-next-line no-restricted-globals
  window.cosCanBootstrapPromise = Promise.all(polyfills).then(() => {
    if (options.zoneless) {
      return;
    }

    const version = '0.16.2';

    // zone.js must be loaded after all of the APIs are loaded before that're
    // likely required to be patched.
    if (ngDevMode) {
      // Load non-minified version of the library in development mode.
      return loadScript(`/assets/js/zone.js/zone.js-${version}.js`);
    } else {
      return loadScript(`/assets/js/zone.js/zone.js-${version}.min.js`);
    }
  });
}
